import {
  cloneElement,
  FC,
  Fragment,
  JSX,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BlockerFunction } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';
import {
  Box,
  BoxProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { isEqual } from 'lodash';

import { HasPermission, useHasPermission } from '@components/auth';
import {
  Button,
  PageContentPadding,
  PageContentTemplate,
  PageContentTemplateProps,
} from '@components/base';
import useDisableBlocker from '@hooks/useDisableBlocker';
import useLayoutEntityUpdateAlert from '@hooks/useLayoutEntityUpdateAlert';
import { ContentType, LayoutEntityType } from '@models/Content';
import LayoutEntity from '@models/LayoutEntity';
import { formatDate, formatTimeAgo } from '@utils/dateUtils';
import { deepClean } from '@utils/filterUtils';
import { removeCrudAttrs, useLayoutEntityName } from '@utils/layoutEntityUtils';
import { reverseName } from '@utils/stringUtils';

import { ReferencedBy } from './ReferencedBy';

export type LayoutEntityPageWrapperProps<T extends LayoutEntity> = PropsWithChildren<
  Omit<PageContentTemplateProps, 'PageTitleWrapperComponent'> & {
    actionButtons: JSX.Element;
    contentType?: ContentType;
    entityType: LayoutEntityType;
    isNew?: boolean;
    mutatedLayoutEntity: (() => T) | T;
    originalLayoutEntity: T;
    sanitizeEntity?: (layoutEntity: T) => T;
    blockerLocationFilter?: BlockerFunction;
  }
>;

export const LayoutEntityContentBox: FC<BoxProps> = ({
  width = 1,
  p = PageContentPadding,
  ...rest
}) => <Box p={p} width={width} {...rest} />;

const BottomActionsWrapper = styled('div')`
  background: ${({ theme }) => theme.palette.background.default};
  border-top: ${({ theme }) => `thin solid ${theme.palette.divider}`};
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  left: 0;
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  position: fixed;
  right: 0;
  z-index: 1;
`;

export const OnlyOnPathnameChange: BlockerFunction = loc =>
  loc.currentLocation.pathname !== loc.nextLocation.pathname;

export function LayoutEntityPageWrapper<T extends LayoutEntity>({
  actionButtons,
  children,
  dense,
  sanitizeEntity = entity => entity,
  contentType: initContentType,
  entityType,
  isNew: isNewEntity,
  mutatedLayoutEntity,
  originalLayoutEntity,
  blockerLocationFilter,
  ...rest
}: LayoutEntityPageWrapperProps<T>) {
  const { t } = useTranslation();
  const getEntityName = useLayoutEntityName();
  const canWrite = useHasPermission();
  const { disableBlocker } = useDisableBlocker();
  const { alert } = useLayoutEntityUpdateAlert();

  const [formattedTimeAgo, setFormattedTimeAgo] = useState(
    originalLayoutEntity.updatedDateTime
      ? formatTimeAgo(originalLayoutEntity.updatedDateTime)
      : undefined
  );

  const ticker = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (ticker.current) clearInterval(ticker.current);

    ticker.current = setInterval(() => {
      if (originalLayoutEntity.updatedDateTime)
        setFormattedTimeAgo(formatTimeAgo(originalLayoutEntity.updatedDateTime));
    }, 1000 * 60);

    return () => {
      if (ticker.current) clearTimeout(ticker.current);
    };
  }, [originalLayoutEntity]);

  const contentType = initContentType ?? entityType;

  const blockerFn = useCallback<BlockerFunction>(
    location => {
      if (blockerLocationFilter?.(location) === false) return false;
      if (disableBlocker) return false;

      // Prompt if deep cleaned objects are not identical
      const mutated =
        typeof mutatedLayoutEntity === 'function' ? mutatedLayoutEntity() : mutatedLayoutEntity;

      return !isEqual(
        deepClean(removeCrudAttrs(sanitizeEntity(mutated))),
        deepClean(removeCrudAttrs(sanitizeEntity(originalLayoutEntity)))
      );
    },
    [
      disableBlocker,
      mutatedLayoutEntity,
      originalLayoutEntity,
      sanitizeEntity,
      blockerLocationFilter,
    ]
  );

  return (
    <Fragment>
      <PageContentTemplate
        {...rest}
        actions={actionButtons}
        contentType={contentType}
        dense={dense}
        pageTitle={
          isNewEntity
            ? `Create ${t(`contentType.${contentType}`).toLocaleLowerCase()}`
            : getEntityName(originalLayoutEntity)
        }
        subheading={
          <Stack direction='row' spacing={1}>
            {formattedTimeAgo && (
              <Typography variant='subtitle2'>
                <Trans
                  components={[
                    <strong title={formatDate(originalLayoutEntity.updatedDateTime)} />,
                    <strong />,
                  ]}
                  i18nKey='entity.lastUpdated'
                  values={{
                    updated: formattedTimeAgo,
                    updatedBy: reverseName(originalLayoutEntity.updatedBy),
                  }}
                />
              </Typography>
            )}

            <ReferencedBy entityId={originalLayoutEntity.id} entityType={entityType} />
          </Stack>
        }
        WrapperProps={{ mb: canWrite ? 8 : undefined }}
      >
        <HasPermission>{dense ? <Box px={PageContentPadding}>{alert}</Box> : alert}</HasPermission>

        {children}

        <HasPermission>
          <BottomActionsWrapper>
            {cloneElement(actionButtons, { disableLoader: true })}
          </BottomActionsWrapper>
        </HasPermission>
      </PageContentTemplate>

      <HasPermission>
        <ReactRouterPrompt when={blockerFn}>
          {({ isActive, onConfirm, onCancel }) => (
            <Dialog fullWidth maxWidth='xs' onClose={onCancel} open={isActive}>
              <DialogTitle>{t('entityPrompt.title')}</DialogTitle>
              <DialogContent>{t('entityPrompt.description')}</DialogContent>
              <DialogActions>
                <Button onClick={onCancel}>{t('cancel')}</Button>
                <Button color='error' onClick={onConfirm} variant='contained'>
                  {t('leave')}
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </ReactRouterPrompt>
      </HasPermission>
    </Fragment>
  );
}
